import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { GrowlerService } from '@netfoundry-ui/shared/growler';
import { Environment, ENVIRONMENT, IpsecTunnelerV2 } from '@netfoundry-ui/shared/model';
import { HTTP_CLIENT, TokenService } from '@netfoundry-ui/shared/services';
import { Observable } from 'rxjs';
import {
    GetOption,
    HateoasResourceOperation,
    PagedGetOption,
    PagedResourceCollection,
} from '@lagoshny/ngx-hateoas-client';
import { tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { AuthorizationService } from '@netfoundry-ui/shared/authorization';
import { Router } from '@angular/router';

/**
 * IpsecTunnelerServiceV2
 *
 * CloudZiti V8 service for the IPsec Tunneler entity. The legacy `*V2` suffix
 * and the `V2/` model directory are kept for compatibility with the
 * HateoasResourceOperation typing — the runtime URL is V3 in CloudZiti
 * because environment.v3Enabled is true and resolves to environment.v3apiUrl
 * (`/core/v3/...`), served by IpsecTunnelerV3Controller.java.
 *
 * Modeled directly on EdgeRouterServiceV2.
 */
@Injectable({ providedIn: 'root' })
export class IpsecTunnelerServiceV2 extends HateoasResourceOperation<IpsecTunnelerV2> {
    static defaultHttpAccept = {
        headers: { accept: 'application/hal+json' },
        pageParams: {
            page: 0,
            size: 2000,
        },
    };
    apiUrl: string;
    lastPageCount = 0;
    lastTotalCount = 0;
    provisionedStatus = 'PROVISIONED';

    constructor(
        @Inject(HTTP_CLIENT) private http: HttpClient,
        private tokenService: TokenService,
        public growlerService: GrowlerService,
        @Inject(ENVIRONMENT) private environment: Environment,
        public dialogForm: MatDialog,
        private authorizationSvc: AuthorizationService,
        private router: Router
    ) {
        super(IpsecTunnelerV2);
        this.apiUrl = environment.v3Enabled ? environment.v3apiUrl : environment.v2apiUrl;
    }

    override getResource(): Observable<IpsecTunnelerV2> {
        throw new Error('Do not use: see getIpsecTunneler');
    }

    override getPage(): Observable<PagedResourceCollection<IpsecTunnelerV2>> {
        throw new Error('Do not use: see getIpsecTunnelerPage');
    }

    getIpsecTunneler(id: string, options: GetOption = {}): Promise<IpsecTunnelerV2 | undefined> {
        return super
            .getResource(id, {
                ...IpsecTunnelerServiceV2.defaultHttpAccept,
                params: { embed: 'host' },
                ...options,
            })
            .toPromise()
            .then((tunneler) => tunneler);
    }

    getIpsecTunnelerPage(options?: PagedGetOption): Promise<IpsecTunnelerV2[]> {
        return super
            .getPage({ ...IpsecTunnelerServiceV2.defaultHttpAccept, ...options })
            .pipe(
                tap((val) => {
                    this.lastPageCount = val.totalPages;
                    this.lastTotalCount = val.totalElements;
                })
            )
            .toPromise()
            .then((tunnelers) => (tunnelers ? tunnelers.resources : []));
    }

    /**
     * `nf-validate` header dry-runs the create against core-management. The
     * `validateCreate` Promise resolves with the validation response (or
     * rejects on a 4xx/5xx). Mirrors EdgeRouterServiceV2.validateCreate.
     */
    public async validateCreate(model: any) {
        const headers = new HttpHeaders().set('Accept', 'application/json').set('nf-validate', '');
        return this.http
            .post(this.apiUrl + 'ipsec-tunnelers', model, {
                headers: headers,
                responseType: 'json',
            })
            .toPromise();
    }

    /**
     * Create an IPsec Tunneler. The server generates the PSK, persists it as a
     * StoredSecret of type IPSEC_PSK, and surfaces the plaintext value once on
     * the create response (`pskRevealedOnce`). The UI must show it once and
     * never re-fetch.
     */
    public create(model: any): Observable<any> {
        return this.http.post(this.apiUrl + 'ipsec-tunnelers', model, {
            headers: new HttpHeaders().set('Accept', 'application/json'),
            responseType: 'json',
        });
    }

    /**
     * Soft-delete: triggers DeleteIpsecTunnelerProcess server-side, which
     * decommissions the OpenZiti identity and tears down the EC2 host.
     */
    public delete(id: string): Observable<any> {
        return this.http.delete(this.apiUrl + 'ipsec-tunnelers/' + id);
    }

    // -----------------------------------------------------------------------
    // Action stubs — endpoints are spec'd in IPSEC_TUNNELER_PLAN.md §3 and §6.
    // Wire them up before enabling the corresponding action menu items.
    // -----------------------------------------------------------------------

    /**
     * GET /v3/ipsec-tunnelers/{id}?meta=configuration
     *
     * Downloads the customer-facing IPsec client configuration document as
     * markdown. The response body is a plaintext markdown file (with attachment
     * Content-Disposition) that contains the plaintext PSK — backed server-side
     * by AuthUpdate (same elevation as PSK rotate).
     *
     * Returns the blob; the caller is responsible for triggering the download.
     */
    public downloadClientConfig(id: string): Observable<Blob> {
        const url = `${this.apiUrl}ipsec-tunnelers/${id}?meta=configuration`;
        return this.http.get(url, {
            headers: new HttpHeaders().set('Accept', 'text/markdown'),
            responseType: 'blob',
        });
    }

    /**
     * TODO: GET /v3/networks/{networkId}/ipsec-tunnelers/{id}/metrics?window=1h|24h|7d
     * Used by the Detail-view "Metrics" tab. Reuse RxTxTimelineChart for the
     * tunneler tx/rx series; add IPsec-specific (rekeys/min, replay drops).
     */
    public getMetrics(id: string, window: '1h' | '24h' | '7d' = '1h'): Observable<any> {
        // TODO: implement once core-management endpoint is deployed.
        const params = new HttpParams().set('window', window);
        const url = `${this.apiUrl}ipsec-tunnelers/${id}/metrics`;
        return this.http.get(url, { params });
    }

    private setHeaders(): HttpHeaders {
        const tokenResponse = this.tokenService.getAccessToken();
        let headers;
        if (!tokenResponse?.expired && tokenResponse?.accessToken) {
            headers = new HttpHeaders().set('Authorization', 'Bearer ' + tokenResponse.accessToken);
        } else {
            headers = new HttpHeaders();
        }
        if (headers != null) {
            return headers.append('Content-Type', 'application/json');
        }
        return headers;
    }
}
